import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/Button';
import BOLayout from '@/pages/back-office/BOLayout';
import Input from '@/components/Input';
import axios from 'axios';
import { toast } from 'react-toastify';
import CountrySelector from '@/components/CountrySelector';
import Dropdown from '@/components/Dropdown';

const OPTIONS_LIMIT = 200;

const DeliveryZoneCreationForm = () => {
	const [canSubmit, setCanSubmit] = useState(false);
	const navigate = useNavigate();
	const [selectedZoneType, setSelectedZoneType] = useState('');
	const [showTextArea, setShowTextArea] = useState(false);
	const [canLoadMoreOptions, setCanLoadMoreOptions] = useState(false);
	const [options, setOptions] = useState([]);
	const [selectedRegions, setSelectedRegions] = useState([]);
	const [loadedOptionsCount, setLoadedOptionsCount] = useState(0);
	const [zoneData, setZoneData] = useState({
		delivery_fee_name: '',
		delivery_fee_type: '',
		delivery_fee_country: '',
		delivery_fee_zones_list: [],
		delivery_fee_price: 0,
		delivery_fee_tier: 0,
	});

	useEffect(() => {
		if (zoneData.delivery_fee_country !== '' && options.length === 0) {
			const fetchRegions = async () => {
				const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/delivery-fees/zones`, {
					params: {
						country: zoneData.delivery_fee_country,
						start: 0,
						limit: OPTIONS_LIMIT,
						withRegionsOnly: true,
					},
				});

				if (response.status === 200) {
					setOptions((prevOptions) => [...prevOptions, ...response.data]);
					setLoadedOptionsCount((prevCount) => prevCount + response.data.length);
				}
			};

			fetchRegions();
		}

		if (
			zoneData.delivery_fee_name &&
			zoneData.delivery_fee_type &&
			zoneData.delivery_fee_price &&
			zoneData.delivery_fee_tier &&
			(zoneData.delivery_fee_type === 'list' ? !zoneData.delivery_fee_country : zoneData.delivery_fee_country)
		) {
			setCanSubmit(true);
			return;
		}

		setCanSubmit(false);
	}, [zoneData]);

	useEffect(() => {
		if (canLoadMoreOptions) {
			const fetchMoreRegions = async () => {
				const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/delivery-fees/zones`, {
					params: {
						country: zoneData.delivery_fee_country,
						start: loadedOptionsCount,
						limit: OPTIONS_LIMIT,
						withRegionsOnly: true,
					},
				});

				if (response.status === 200) {
					setOptions((prevOptions) => [...prevOptions, ...response.data]);
					setLoadedOptionsCount((prevCount) => prevCount + response.data.length);
				}
			};
			fetchMoreRegions();

			setCanLoadMoreOptions(false);
		}
	}, [canLoadMoreOptions]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;

		if (['country', 'region'].includes(selectedZoneType)) {
			setZoneData({ ...zoneData, [name]: value });

			return;
		}

		setZoneData({ ...zoneData, [name]: value });
	};

	const handleCountryChange = async (e) => {
		setZoneData({ ...zoneData, delivery_fee_country: e.target.value });
	};

	const handleRegionChange = async (option) => {
		if (option !== '' && !selectedRegions.some((region) => region.value === option)) {
			setSelectedRegions((prevState) => [
				...prevState,
				{
					name: option.region,
					value: option.region,
				},
			]);

			setZoneData((prevState) => ({
				...prevState,
				delivery_fee_zones_list: [...prevState.delivery_fee_zones_list, option],
			}));
		}
	};

	const handleRadioChange = (e) => {
		const { value } = e.target;

		// Si un type différent est sélectionné et qu'il y a déjà des régions sélectionnées, on les efface
		if (selectedRegions.length > 0 && value !== 'region') {
			setSelectedRegions([]);
		}

		// Réinitialise la liste des zones lorsque le type change
		setZoneData((prevState) => ({
			...prevState,
			delivery_fee_type: value,
			delivery_fee_zones_list: [], // Réinitialiser la liste
		}));

		// Affiche ou non la zone de texte en fonction de la sélection
		setShowTextArea(value === 'list');
		setSelectedZoneType(value);

		if (value === 'list') {
			setOptions([]);
			setZoneData((prevState) => ({
				...prevState,
				delivery_fee_country: '',
			}));
		}
	};

	const handleTextAreaChange = (e) => {
		const values = e.target.value.split('\n'); // Liste des valeurs actuelles (lignes y compris celles vides)

		if (values.length === 1 && values[0] === '') {
			setZoneData((prevState) => ({
				...prevState,
				delivery_fee_zones_list: [],
			}));
			return;
		}

		// Si l'utilisateur supprime un caractère (Backspace)
		if (e.key === 'Backspace') {
			const cursorPosition = e.target.selectionStart; // Position du curseur dans le texte
			const previousValue = zoneData.delivery_fee_zones_list.join('\n'); // Récupère l'ancien texte
			const lastCharacterBeforeCursor = e.target.value[cursorPosition - 1];

			// Si un caractère est supprimé
			if (lastCharacterBeforeCursor === undefined && cursorPosition !== previousValue.length) {
				// Vérifier si une ligne complète a été supprimée
				const diff = previousValue.split('\n').filter((line) => !values.includes(line));

				if (diff.length > 0) {
					const updatedZonesList = zoneData.delivery_fee_zones_list.filter((zone) => !diff.includes(zone));

					setZoneData((prevState) => ({
						...prevState,
						delivery_fee_zones_list: updatedZonesList,
					}));
				}
			}
		} else {
			// Mettre à jour la liste lors de l'ajout ou modification de texte
			setZoneData((prevState) => ({
				...prevState,
				delivery_fee_zones_list: values
					.map((line) => {
						return {
							zipcode: line,
						};
					})
					.filter(Boolean), // Supprime les lignes vides
			}));
		}
	};

	const handleRemoveRegion = (region) => {
		setSelectedRegions(selectedRegions.filter((r) => r.value !== region.value));

		setZoneData((prevState) => {
			let updatedZonesList = [];

			if (zoneData.delivery_fee_type === 'region') {
				updatedZonesList = prevState.delivery_fee_zones_list.filter((r) => r.region !== region.name);
			}

			return {
				...prevState,
				delivery_fee_zones_list: updatedZonesList,
			};
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const formData = new FormData();
		formData.append('name', zoneData.delivery_fee_name);
		formData.append('type', zoneData.delivery_fee_type);
		formData.append('country', zoneData.delivery_fee_country);
		formData.append('zones', JSON.stringify(zoneData.delivery_fee_zones_list));
		formData.append('price', zoneData.delivery_fee_price);
		formData.append('tier', zoneData.delivery_fee_tier);

		try {
			const response = await axios.post(
				`${import.meta.env.VITE_API_URL}/api/back-office/delivery-fees/create`,
				formData
			);

			if (response.status === 201) {
				toast.success('Frais de livraison créés avec succès', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
		} catch (error) {
			console.error('Error creating category:', error);

			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 800,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}

		setTimeout(() => {
			navigate('/back-office/settings/delivery_zones/list');
		}, 1000);
	};

	return (
		<BOLayout>
			<button
				type="button"
				className="mb-4 flex items-center gap-2"
				onClick={() => navigate(`/back-office/settings/delivery_zones/list`)}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 24 24"
					strokeWidth={1.5}
					stroke="currentColor"
					className="w-6 h-6"
				>
					<path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
				</svg>
				Retour à la liste des frais de livraison
			</button>

			<h1 className="text-center text-3xl font-bold mb-12">Créez une nouvelle zone de livraison</h1>

			<form className="w-3/5 mx-auto">
				{selectedRegions.length > 0 && (
					<div className="grid grid-cols-4 gap-2 mb-4">
						{selectedRegions.map((regionObj) => (
							<button
								key={regionObj.value}
								type="button"
								onClick={() => handleRemoveRegion(regionObj)}
								className="transition duration-300 ease-in-out capitalize font-bold text-xs bg-black border border-black text-white p-2 rounded-md flex items-center gap-2 hover:cursor-pointer hover:bg-white hover:text-black"
							>
								<span>x</span> {zoneData.delivery_fee_type !== 'region' ? `${regionObj.value}` : `${regionObj.name}`}
							</button>
						))}
					</div>
				)}

				<div className="grid grid-cols-3 place-items-center gap-4">
					<div className="flex items-center gap-2">
						<Input
							type="radio"
							id="country"
							name="zone_type"
							value="country"
							handleChange={handleRadioChange}
							customClass="m-0"
							withDiv={false}
						/>
						<label htmlFor="country">Pays</label>
					</div>
					<div className="flex items-center gap-2">
						<Input
							type="radio"
							id="region"
							name="zone_type"
							value="region"
							handleChange={handleRadioChange}
							customClass="m-0"
							withDiv={false}
						/>
						<label htmlFor="region">Région</label>
					</div>
					<div className="flex items-center gap-2">
						<Input
							type="radio"
							id="list"
							name="zone_type"
							value="list"
							handleChange={handleRadioChange}
							customClass="m-0"
							withDiv={false}
						/>
						<label htmlFor="list">Plage de CP</label>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="my-5 w-full">
						<CountrySelector
							emptyRef={showTextArea}
							id="delivery_fee_country"
							name="delivery_fee_country"
							onChange={handleCountryChange}
							options={[
								{ value: 'France', label: 'France' },
								{ value: 'Belgique', label: 'Belgique' },
								{ value: 'Luxembourg', label: 'Luxembourg' },
								{ value: 'Monaco', label: 'Monaco' },
							]}
							isDisabled={!['country', 'region'].includes(selectedZoneType)}
							isRequired={true}
						/>
					</div>
					<div>
						<div className="my-5 w-full relative">
							<Dropdown
								buttonText="Choisissez une région"
								placeholder="Choisissez une région"
								options={options}
								isObject={true}
								classes={`p-2 border border-gray-300 rounded-md w-full flex justify-between items-center overflow-y-auto ${!['region'].includes(selectedZoneType) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : ''
									}`}
								isDisabled={!['region'].includes(selectedZoneType)}
								multiKeys={['region']}
								setCanLoadMoreOptions={setCanLoadMoreOptions}
								customHeightClass="h-[300px]"
								onOptionSelect={handleRegionChange}
							/>
						</div>
					</div>
				</div>

				{showTextArea && (
					<div className="my-5 w-full">
						<textarea
							id="delivery_fee_zones_list"
							name="delivery_fee_zones_list"
							placeholder={'Codes postaux'}
							className="col-span-2 py-1 px-4 border border-gray-300 rounded-md resize-none w-full"
							onChange={handleTextAreaChange}
							rows="5"
							cols={'50'}
						/>
					</div>
				)}

				<div className="grid grid-cols-1 gap-4">
					<Input
						id={'delivery_fee_name'}
						name={'delivery_fee_name'}
						type={'text'}
						placeholder={'Nom des frais'}
						labelText={'Nom des frais'}
						labelFor={'delivery_fee_name'}
						isRequired={true}
						handleChange={handleInputChange}
						fullWidthInput={true}
					/>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<Input
						id={'delivery_fee_price'}
						name={'delivery_fee_price'}
						type={'numeric'}
						placeholder={'Prix'}
						labelText={'Nom des frais'}
						labelFor={'delivery_fee_price'}
						isRequired={true}
						handleChange={handleInputChange}
						fullWidthInput={true}
					/>

					<Input
						id={'delivery_fee_tier'}
						name={'delivery_fee_tier'}
						type={'numeric'}
						placeholder={'Pallier'}
						labelText={'Nom des frais'}
						labelFor={'delivery_fee_tier'}
						isRequired={true}
						handleChange={handleInputChange}
						fullWidthInput={true}
					/>
				</div>

				<Button
					disabled={canSubmit}
					action={'submit'}
					handleClick={handleSubmit}
					text="Enregister"
					withTopMargin={false}
				/>
			</form>
		</BOLayout>
	);
};

export default DeliveryZoneCreationForm;
